import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { brandInfo, testimonials, faqs } from '@/mocks/movies';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const aboutStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于鲁鲁影院',
    description: brandInfo.description,
    url: 'https://lulucinema.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '鲁鲁影院',
      url: 'https://lulucinema.com',
    },
    mainEntity: {
      '@type': 'Organization',
      name: brandInfo.name,
      description: brandInfo.description,
      url: 'https://lulucinema.com',
    },
  };

  return (
    <>
      <SEOHead
        title="关于鲁鲁影院 - 高清免费在线不缓冲影视平台_品牌介绍_联系我们"
        description="鲁鲁影院品牌介绍：致力于为广大影迷提供最优质的在线观影体验。海量正版影视资源，高清画质、流畅播放、完全免费。了解鲁鲁影院的品牌故事、核心优势和联系方式。"
        keywords="鲁鲁影院,关于我们,品牌介绍,免费影视平台,在线观看,高清免费,联系方式"
        canonicalUrl="https://lulucinema.com/about"
        structuredData={aboutStructuredData}
      />

      <div className="min-h-screen bg-background-100">
        <Navbar />

        <main className="pt-16">
          <div className="container-main">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          <section className="py-10 md:py-16">
            <div className="container-main text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-50 mb-4">
                关于鲁鲁影院
              </h1>
              <p className="text-lg text-foreground-400 max-w-3xl mx-auto leading-relaxed">
                {brandInfo.slogan} — {brandInfo.description}
              </p>
            </div>
          </section>

          <section className="py-10 md:py-14 bg-background-50">
            <div className="container-main">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-100 text-center mb-2">
                <a href="#brand-story">品牌故事</a>
              </h2>
              <p className="text-foreground-500 text-center mb-10 max-w-2xl mx-auto">
                自{brandInfo.established}成立以来，鲁鲁影院始终秉持"让每个人都能免费看好片"的初心
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
                {[
                  { value: brandInfo.users, label: '服务用户' },
                  { value: brandInfo.content, label: '影视资源' },
                  { value: brandInfo.dailyUpdate, label: '更新频率' },
                  { value: '99.9%', label: '服务可用率' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-4 md:p-6 rounded-xl bg-background-100 border border-background-200/50">
                    <div className="text-2xl md:text-3xl font-bold text-primary-400 mb-1">{stat.value}</div>
                    <div className="text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {brandInfo.features.map((feature) => (
                  <div key={feature.title} className="p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/50">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/15 flex items-center justify-center mb-3">
                      <i className={`${feature.icon} text-lg text-primary-400 w-5 h-5 flex items-center justify-center`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-100 mb-1.5">{feature.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14">
            <div className="container-main">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-100 text-center mb-2">
                <a href="#testimonials">用户口碑</a>
              </h2>
              <p className="text-foreground-500 text-center mb-10">来自真实用户的评价</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {testimonials.map((t) => (
                  <div key={t.id} className="p-5 md:p-6 rounded-xl bg-background-200/50 border border-background-300/30">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={t.avatar}
                        alt={t.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="text-sm font-medium text-foreground-200">{t.name}</div>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: t.rating }).map((_, i) => (
                            <i key={i} className="ri-star-fill text-primary-400 text-xs w-3 h-3 flex items-center justify-center"></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-400 leading-relaxed">{t.content}</p>
                    <p className="text-xs text-foreground-600 mt-3">{t.date}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <FAQSection />
          <ContactSection />
        </main>

        <Footer />
      </div>
    </>
  );
}

function FAQSection() {
  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <section className="py-10 md:py-14 bg-background-50">
      <script type="application/ld+json">{JSON.stringify(faqStructuredData)}</script>
      <div className="container-main max-w-3xl">
        <h2 className="text-xl md:text-2xl font-bold text-foreground-100 text-center mb-2">
          <a href="#faq">常见问题</a>
        </h2>
        <p className="text-foreground-500 text-center mb-10">关于鲁鲁影院的常见疑问解答</p>

        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <details key={idx} className="group bg-background-100 rounded-xl border border-background-200/50 overflow-hidden">
              <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                <span className="text-sm font-medium text-foreground-200 pr-4">{faq.question}</span>
                <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform w-4 h-4 flex items-center justify-center shrink-0"></i>
              </summary>
              <div className="px-4 md:px-5 pb-4 md:pb-5">
                <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  return (
    <section className="py-10 md:py-14">
      <div className="container-main max-w-3xl">
        <h2 className="text-xl md:text-2xl font-bold text-foreground-100 text-center mb-2">
          <a href="#contact-us">联系我们</a>
        </h2>
        <p className="text-foreground-500 text-center mb-10">有疑问或建议？欢迎联系我们</p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
          <div className="p-5 rounded-xl bg-background-200/50 border border-background-300/30 text-center">
            <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-primary-500/15 flex items-center justify-center">
              <i className="ri-mail-line text-lg text-primary-400 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <h3 className="text-sm font-semibold text-foreground-200 mb-1">电子邮箱</h3>
            <p className="text-xs text-foreground-500">{brandInfo.contact.email}</p>
          </div>
          <div className="p-5 rounded-xl bg-background-200/50 border border-background-300/30 text-center">
            <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-accent-500/15 flex items-center justify-center">
              <i className="ri-wechat-line text-lg text-accent-400 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <h3 className="text-sm font-semibold text-foreground-200 mb-1">微信公众号</h3>
            <p className="text-xs text-foreground-500">{brandInfo.contact.wechat}</p>
          </div>
          <div className="p-5 rounded-xl bg-background-200/50 border border-background-300/30 text-center">
            <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-secondary-500/15 flex items-center justify-center">
              <i className="ri-weibo-line text-lg text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
            </div>
            <h3 className="text-sm font-semibold text-foreground-200 mb-1">新浪微博</h3>
            <p className="text-xs text-foreground-500">{brandInfo.contact.weibo}</p>
          </div>
        </div>
      </div>
    </section>
  );
}